import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import { Redirect, Route } from "wouter";

export function ProtectedRoute({
  path,
  component: Component,
}: {
  path: string;
  component: React.ComponentType;
}) {
  const { user, isLoading, isStaffPickingSidekick } = useAuth();

  if (isLoading) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-border" />
        </div>
      </Route>
    );
  }

  if (!user) {
    return (
      <Route path={path}>
        <Redirect to={isStaffPickingSidekick ? "/select-sidekick" : "/auth"} />
      </Route>
    );
  }

  return <Route path={path} component={Component} />;
}

/**
 * Like ProtectedRoute, but additionally hidden behind a per-user feature flag.
 * While auth is loading it shows the loader (so a bookmarked flagged path never
 * flashes NotFound); once loaded, an authed user without the flag falls through
 * to the app's NotFound, and an unauthed user is redirected as usual.
 */
export function FlaggedRoute({
  path,
  component: Component,
  enabled,
}: {
  path: string;
  component: React.ComponentType;
  enabled: (user: NonNullable<ReturnType<typeof useAuth>["user"]>) => boolean;
}) {
  const { user, isLoading, isStaffPickingSidekick } = useAuth();

  if (isLoading) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-border" />
        </div>
      </Route>
    );
  }

  if (!user) {
    return (
      <Route path={path}>
        <Redirect to={isStaffPickingSidekick ? "/select-sidekick" : "/auth"} />
      </Route>
    );
  }

  // Feature disabled for this user: don't register the route, let it fall
  // through to the Switch's NotFound.
  if (!enabled(user)) return null;

  return <Route path={path} component={Component} />;
}
